import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import {
  formatProductDate,
  formatProductMoney,
} from '#components/product-finance/money';

import {
  EmptySection,
  occurrenceTarget,
  SectionCard,
  SummaryAmount,
} from './dashboard-shared';
import type { ActiveCommand } from './FinancialDashboardPage';

export function CycleCard({
  dashboard,
}: {
  dashboard: ReadyFinancialDashboard;
}) {
  return (
    <SectionCard title={<Trans>Current cycle</Trans>}>
      {dashboard.currentCycle === null ? (
        <EmptySection>
          <Trans>No current budget cycle.</Trans>
        </EmptySection>
      ) : (
        <View style={{ gap: 6 }}>
          <strong style={{ fontSize: 20 }}>
            {dashboard.currentCycle.budgetCycle.name}
          </strong>
          <span>
            <Trans>Started</Trans>:{' '}
            {formatProductDate(dashboard.currentCycle.fundingDate)}
          </span>
          <span style={{ color: theme.pageTextSubdued }}>
            <Trans>Next cycle</Trans>:{' '}
            {dashboard.nextCycle === null ? (
              <Trans>Not scheduled</Trans>
            ) : (
              <>
                {dashboard.nextCycle.budgetCycle.name} ·{' '}
                {formatProductDate(dashboard.nextCycle.fundingDate)}
              </>
            )}
          </span>
          {dashboard.currentCycleOverview !== null && (
            <View
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 6,
                marginTop: 8,
              }}
            >
              <SummaryAmount
                label={<Trans>Expected income</Trans>}
                money={dashboard.currentCycleOverview.expectedIncome}
              />
              <SummaryAmount
                label={<Trans>Received</Trans>}
                money={dashboard.currentCycleOverview.receivedIncome}
              />
              <SummaryAmount
                label={<Trans>Commitments</Trans>}
                money={dashboard.currentCycleOverview.commitmentTotal}
              />
              <SummaryAmount
                label={<Trans>Paid</Trans>}
                money={dashboard.currentCycleOverview.paidCommitments}
              />
              <SummaryAmount
                label={<Trans>Pending</Trans>}
                money={dashboard.currentCycleOverview.pendingCommitments}
              />
              <SummaryAmount
                label={<Trans>Recorded expenses</Trans>}
                money={dashboard.currentCycleOverview.actualExpense}
              />
            </View>
          )}
        </View>
      )}
    </SectionCard>
  );
}

export function NextIncomeCard({
  dashboard,
  onOpenCommand,
}: {
  dashboard: ReadyFinancialDashboard;
  onOpenCommand: (command: ActiveCommand) => void;
}) {
  return (
    <SectionCard title={<Trans>Next reliable income</Trans>}>
      {dashboard.nextReliableIncome === null ? (
        <EmptySection>
          <Trans>No fixed expected income in this planning window.</Trans>
        </EmptySection>
      ) : (
        <View style={{ gap: 10 }}>
          <FinancialText as="strong" style={{ fontSize: 22 }}>
            {formatProductMoney(
              dashboard.nextReliableIncome.totalExpectedAmount,
            )}
          </FinancialText>
          <span>{formatProductDate(dashboard.nextReliableIncome.date)}</span>
          {dashboard.nextReliableIncome.incomes.map(income => (
            <View
              key={income.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <span>{income.name}</span>
              <Button
                onPress={() =>
                  onOpenCommand({
                    kind: 'expected-income',
                    target: occurrenceTarget(income),
                  })
                }
                style={{ minHeight: 38 }}
              >
                <Trans>Register income</Trans>
              </Button>
            </View>
          ))}
        </View>
      )}
    </SectionCard>
  );
}
